import React, { useEffect, useRef, useState } from "react";
import { FormControl } from "@mui/material";
import styled from "styled-components";
import useTheme from "../../../../common/hooks/useTheme";
import UpdatedBadge from "./UpdatedBadge";
import {
  loadGoogleMapsPlaces,
  parseAddressComponents,
  ParsedAddress,
} from "../../../../common/utils/googleMaps";
import FieldHintMessage from "./FieldHintMessage";

// Wait this long after the last keystroke before asking Google for predictions,
// and don't query at all below this many characters. Together these keep the
// per-request Places calls (and their cost) down while typing.
const DEBOUNCE_MS = 350;
const MIN_QUERY_LEN = 3;

const Label = styled.label<{ theme: any; $active: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  font-family: var(--font-Medium), sans-serif;
  font-size: 10px;
  pointer-events: none;
  transform: translate(12px, 10px) scale(1);
  z-index: 1;
  color: ${({ theme, $active }) =>
    $active ? theme.colors_ds.PRIMARY_ICON_MAIN : theme.colors_ds.NEUTRAL_TEXT_WEAK};
`;

const boxBase = `
  width: 100%;
  height: 56px;
  padding: 24px 40px 8px 12px;
  border-radius: 8px;
  font-family: var(--font-Medium), sans-serif;
  font-size: 12px;
  box-sizing: border-box;
`;

const DisplayBox = styled.div<{ theme: any }>`
  ${boxBase}
  display: flex;
  align-items: center;
  cursor: text;
  border: 1px solid ${({ theme }) => theme.colors_ds.NEUTRAL_BORDER_WEAK};
  background-color: ${({ theme }) => theme.colors_ds.NEUTRAL_BG_MAIN};
  color: ${({ theme }) => theme.colors_ds.NEUTRAL_TEXT_DEFAULT};
  &:hover {
    border-color: ${({ theme }) => theme.colors_ds.NEUTRAL_BORDER_DEFAULT};
  }
`;

const Input = styled.input<{ theme: any }>`
  ${boxBase}
  outline: none;
  border: 1px solid ${({ theme }) => theme.colors_ds.PRIMARY_ICON_MAIN};
  background-color: ${({ theme }) => theme.colors_ds.NEUTRAL_BG_MAIN};
  color: ${({ theme }) => theme.colors_ds.NEUTRAL_TEXT_DEFAULT};
`;

const SuggestionList = styled.ul<{ theme: any }>`
  position: absolute;
  top: 60px;
  left: 0;
  right: 0;
  margin: 0;
  padding: 4px;
  list-style: none;
  z-index: 20;
  max-height: 240px;
  overflow-y: auto;
  background-color: #fff;
  border: 1px solid ${({ theme }) => theme.colors_ds.NEUTRAL_BORDER_WEAK};
  border-radius: 8px;
  box-shadow: 0 8px 20px 0 rgba(0, 0, 0, 0.06), 0 2px 8px 0 rgba(0, 0, 0, 0.04);
`;

const SuggestionItem = styled.li<{ theme: any; $active: boolean }>`
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-family: var(--font-Medium), sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors_ds.NEUTRAL_TEXT_DEFAULT};
  background-color: ${({ theme, $active }) =>
    $active ? theme.colorTokens.P[10] : "transparent"};
  &:hover {
    background-color: ${({ theme }) => theme.colorTokens.P[10]};
  }
`;

const SearchIcon = ({ color }: { color: string }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}
  >
    <path
      d="M11.74 10.34a6.5 6.5 0 1 0-1.4 1.4l3.86 3.85a1 1 0 0 0 1.42-1.42l-3.85-3.85a1 1 0 0 0-.03.02zM6.5 12a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z"
      fill={color}
    />
  </svg>
);

interface AddressAutocompleteFieldProps {
  value: string;
  label?: string;
  // Passed in rather than read from Redux here: this field is presentational
  // (value in, selection out) and the parent already holds the answers.
  isUpdated?: boolean;
  // Only a picked Places suggestion commits a change — typing alone never does.
  onSelect: (parsed: ParsedAddress) => void;
  // Reports which parts are missing from the current selection (street number /
  // postal code) so the parent can gate Save on a complete address.
  onCompletenessChange?: (missingParts: string[]) => void;
}

const AddressAutocompleteField: React.FC<AddressAutocompleteFieldProps> = ({
  value,
  label = "Address",
  isUpdated = false,
  onSelect,
  onCompletenessChange,
}) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimer = useRef<any>(null);
  const debounceTimer = useRef<any>(null);
  // Google Places handle, resolved once per edit session.
  const googleRef = useRef<any>(null);
  // One session token spans all keystroke suggestions + the final details fetch
  // so a completed lookup is billed as a single Autocomplete session.
  const sessionTokenRef = useRef<any>(null);
  // The address string we last committed via a selection. Lets us tell our own
  // value updates apart from external ones (e.g. a parent "Cancel" revert), so
  // the missing-parts warning survives our onSelect round-trip but clears when
  // the address is reverted externally.
  const lastSelectedAddressRef = useRef<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? "");
  const [predictions, setPredictions] = useState<any[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Parts missing from the picked address (street number / postal code) — nudges
  // the affiliate to search again and choose a more specific, complete result.
  const [missingParts, setMissingParts] = useState<string[]>([]);

  // Reflect external changes (e.g. profile load) into the field.
  useEffect(() => {
    setInputValue(value ?? "");
    // A value change we didn't originate (profile load, or a parent "Cancel"
    // reverting the form) means the previous missing-parts warning is stale —
    // clear it. Our own selection round-trips through here with a matching
    // value, so its warning is preserved.
    if ((value ?? "") !== (lastSelectedAddressRef.current ?? "")) {
      setMissingParts([]);
    }
  }, [value]);

  // Surface the current missing-parts to the parent (for Save gating).
  useEffect(() => {
    onCompletenessChange?.(missingParts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingParts]);

  // Spin up the Places services (and a fresh session token) on entering edit mode.
  useEffect(() => {
    if (!editing) return;
    let cancelled = false;

    const focusInput = () => inputRef.current?.focus();

    loadGoogleMapsPlaces()
      .then((google: any) => {
        if (cancelled) return;
        googleRef.current = google;
        sessionTokenRef.current =
          new google.maps.places.AutocompleteSessionToken();
        focusInput();
      })
      .catch((err) => {
        // No key / failed load → stays a plain editable text input.
        if (!cancelled) focusInput();
        console.warn("Address autocomplete unavailable:", err?.message || err);
      });

    return () => {
      cancelled = true;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const requestPredictions = async (query: string) => {
    if (query.trim().length < MIN_QUERY_LEN) {
      setPredictions([]);
      return;
    }
    try {
      // Ensure Places is loaded (covers typing before the edit-mode load effect
      // has resolved) and that a session token exists.
      const google = googleRef.current || (await loadGoogleMapsPlaces());
      googleRef.current = google;
      if (!sessionTokenRef.current) {
        sessionTokenRef.current =
          new google.maps.places.AutocompleteSessionToken();
      }
      const { suggestions } =
        await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          {
            input: query,
            sessionToken: sessionTokenRef.current,
            // Force English results regardless of the browser/OS locale.
            language: "en",
          }
        );
      setPredictions(
        (suggestions ?? []).filter((s: any) => s.placePrediction)
      );
      setActiveIndex(-1);
    } catch (err) {
      setPredictions([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setInputValue(query);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(
      () => requestPredictions(query),
      DEBOUNCE_MS
    );
  };

  const selectPrediction = async (suggestion: any) => {
    const google = googleRef.current;
    if (!google || !suggestion?.placePrediction) return;
    if (blurTimer.current) clearTimeout(blurTimer.current);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    try {
      // Construct the Place from its id (more robust than toPlace(), which can
      // return undefined on some Maps builds), then fetch its address fields.
      const place = new google.maps.places.Place({
        id: suggestion.placePrediction.placeId,
        // English components regardless of the browser/OS locale.
        requestedLanguage: "en",
      });
      await place.fetchFields({ fields: ["addressComponents"] });
      const parsed = parseAddressComponents(place.addressComponents || []);
      setInputValue(parsed.address);
      const missing: string[] = [];
      if (!parsed.hasStreetNumber) missing.push("street number");
      if (!parsed.zip) missing.push("postal code");
      setMissingParts(missing);
      // Remember this as our own commit so the value-sync effect doesn't treat
      // the resulting prop update as an external revert and wipe the warning.
      lastSelectedAddressRef.current = parsed.address;
      onSelect(parsed);
      setPredictions([]);
      setEditing(false);
      // Fetching details closes the billing session; open a fresh one for next.
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
    } catch (err) {
      console.warn("Address details unavailable:", err);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!predictions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, predictions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) selectPrediction(predictions[activeIndex]);
    } else if (e.key === "Escape") {
      setPredictions([]);
    }
  };

  return (
    <div style={{ position: "relative" }}>
    <FormControl variant="outlined" fullWidth sx={{ position: "relative" }}>
      <Label theme={theme} $active={editing}>
        {editing ? "Search address" : label}
        {!editing && isUpdated && <UpdatedBadge />}
      </Label>
      {editing ? (
        <>
          <Input
            ref={inputRef}
            theme={theme}
            value={inputValue}
            autoComplete="off"
            placeholder="Start typing your address"
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (blurTimer.current) clearTimeout(blurTimer.current);
            }}
            onBlur={() => {
              // Blur without a selection commits nothing — revert to the last
              // saved value so a half-typed query can't overwrite the address.
              blurTimer.current = setTimeout(() => {
                setInputValue(value ?? "");
                setPredictions([]);
                setEditing(false);
              }, 200);
            }}
          />
          <SearchIcon color={theme.colors_ds.PRIMARY_ICON_MAIN} />
          {predictions.length > 0 && (
            <SuggestionList theme={theme}>
              {predictions.map((p, i) => (
                <SuggestionItem
                  key={p.placePrediction?.placeId ?? i}
                  theme={theme}
                  $active={i === activeIndex}
                  // Keep the input focused so its blur-revert doesn't fire before
                  // the click selects the prediction.
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => selectPrediction(p)}
                >
                  {p.placePrediction?.text?.text ?? ""}
                </SuggestionItem>
              ))}
            </SuggestionList>
          )}
        </>
      ) : (
        <>
          <DisplayBox
            theme={theme}
            role="button"
            tabIndex={0}
            onClick={() => {
              // Keep the current address so the user can edit it in place
              // instead of starting from an empty field.
              setInputValue(value ?? "");
              setEditing(true);
            }}
            onFocus={() => {
              setInputValue(value ?? "");
              setEditing(true);
            }}
          >
            {inputValue || (
              <span style={{ color: theme.colors_ds.NEUTRAL_TEXT_WEAK }}>
                Search address
              </span>
            )}
          </DisplayBox>
          <SearchIcon color={theme.colors_ds.NEUTRAL_TEXT_WEAK} />
        </>
      )}
    </FormControl>
      {missingParts.length > 0 && (
        <FieldHintMessage placement="gap">
          No {missingParts.join(" or ")} found — pick a more specific address.
        </FieldHintMessage>
      )}
    </div>
  );
};

export default AddressAutocompleteField;
